/**
 * Which clock a time is read on.
 *
 * Two zones, and every time this app shows or cuts belongs to exactly one:
 *
 *   - **The project's.** A project's sales, tasks, appointments, reports and
 *     dashboards are read by the sales office that runs it, so "today", "this
 *     month" and "2:15 p.m." are that office's: Central time on a Texas
 *     project, Pacific on a BC one. The zone comes from the project's
 *     province or state, the same field that picks its jurisdiction pack.
 *   - **The platform's (Toronto).** What belongs to SaleFish rather than to
 *     any one project: platform reports, usage, the user log, billing
 *     deadlines, release notes, the SaleFish Legacy vendor's own wall clock,
 *     and the 08:xx daily cron. Those stay Toronto whatever project is open.
 *
 * A project with no province (or one we have no pack for) is read in Toronto,
 * which is what every project was before projects left Ontario.
 *
 * Kept apart from the jurisdiction packs, which carry law the client never
 * needs to draw a time: tests/timeZones.test.ts pins this table to each pack's
 * own `timeZone`, so the two cannot drift.
 */

import { regionCode } from "./address";

/** SaleFish's own clock: platform-wide reports, billing, crons, the Legacy vendor. */
export const PLATFORM_TIME_ZONE = "America/Toronto";

/** A project read before its province is known, or in one with no pack. */
export const DEFAULT_PROJECT_TIME_ZONE = PLATFORM_TIME_ZONE;

/** Province or state code → the zone its jurisdiction pack names. */
export const REGION_TIME_ZONES: Readonly<Record<string, string>> = {
  AB: "America/Edmonton",
  BC: "America/Vancouver",
  MB: "America/Winnipeg",
  NB: "America/Moncton",
  NL: "America/St_Johns",
  NS: "America/Halifax",
  NT: "America/Yellowknife",
  NU: "America/Iqaluit",
  ON: "America/Toronto",
  PE: "America/Halifax",
  QC: "America/Toronto",
  SK: "America/Regina",
  YT: "America/Whitehorse",
  FL: "America/New_York",
  TX: "America/Chicago",
};

/** The zone a project's own times are read in, from its stored province or state ("TX", "Texas"). */
export function projectTimeZone(region: string | null | undefined): string {
  const code = regionCode(region);
  return (code && REGION_TIME_ZONES[code]) || DEFAULT_PROJECT_TIME_ZONE;
}

const ymdFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * The calendar day an instant falls on in a zone, YYYY-MM-DD. en-CA prints a
 * numeric date in ISO order; `toISOString().slice(0, 10)` would be the UTC
 * day, which in the evening in North America is already tomorrow.
 */
export function zonedYmd(at: Date, timeZone: string): string {
  let f = ymdFormats.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
    ymdFormats.set(timeZone, f);
  }
  return f.format(at);
}

/** Milliseconds the zone is ahead of UTC at that instant (Toronto: −4 h or −5 h; St. John's: −2.5 h or −3.5 h). */
export function zoneOffsetMs(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return wall - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * The instant a wall-clock time in a zone names ("2026-10-25", "09:00" in
 * Chicago → 14:00 UTC). Two passes, so a time on a daylight-saving changeover
 * day lands on the right side of it.
 */
export function zonedInstant(day: string, time: string, timeZone: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  const [hh, mm] = (time || "00:00").split(":").map(Number);
  const wall = Date.UTC(y, m - 1, d, hh || 0, mm || 0);
  let at = wall - zoneOffsetMs(new Date(wall), timeZone);
  at = wall - zoneOffsetMs(new Date(at), timeZone);
  return new Date(at);
}

/** The instant a calendar day begins in a zone. */
export function zonedMidnight(day: string, timeZone: string): Date {
  return zonedInstant(day, "00:00", timeZone);
}

/**
 * The zone as people say it, in the reader's language: "Central Time",
 * "heure du Centre", "Eastern Time". For a label beside a time ("Generated
 * 2:15 p.m. (Central Time)"), so no zone name needs a translation key.
 */
export function timeZoneLabel(timeZone: string, locale = "en-CA", at: Date = new Date()): string {
  for (const timeZoneName of ["longGeneric", "long"] as const) {
    try {
      const name = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName }).formatToParts(at).find((p) => p.type === "timeZoneName")?.value;
      if (name) return name;
    } catch {
      // An engine without longGeneric falls through to the specific name.
    }
  }
  return timeZone;
}
